// src/services/cesionesService.jsx

const API_BASE_URL = process.env.REACT_APP_API_URL || '';

/**
 * Obtener información de una cesión
 * POST /api/Cesiones/getCessionInformation
 */
export const getCessionInformation = async ({ group = '', order = '', cessionID = '', sellerID = '' }) => {
  const response = await fetch(`${API_BASE_URL}/api/Cesiones/getCessionInformation`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ group, order, cessionID, sellerID }),
  });

  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.message || `Error al consultar la cesión: ${response.status}`);
  }

  const data = await response.json();
  if (data.errMsg) throw new Error(data.errMsg);
  return data;
};

/**
 * Subir documento de cesión en base64
 * POST /api/Cesiones/uploadCessionDocument
 */
export const uploadCessionDocument = async ({ idCession, documentName, documentB64 }) => {
  const response = await fetch(`${API_BASE_URL}/api/Cesiones/uploadCessionDocument`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ idCession, documentName, documentB64 }),
  });

  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.message || `Error al subir documento: ${response.status}`);
  }

  return response.json();
};

/**
 * Actualizar estado de una cesión
 * POST /api/Cesiones/setCessionStatus
 */
export const setCessionStatus = async ({ idCession, cessionStatus, statusNotes = '', userID = 0 }) => {
  const response = await fetch(`${API_BASE_URL}/api/Cesiones/setCessionStatus`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ idCession, cessionStatus, statusNotes, userID }),
  });

  if (!response.ok) {
    const err = await response.json().catch(() => ({}));

    if(response.status === 403){
      throw new Error(`Error al actualizar estado. La cesión no se encuentra en el estado esperado. Se recargará la página para ver el estado actualizado.`);
    } else {
      throw new Error(err.message || `Error al actualizar estado: ${response.status}`);
    }
  }

  return response.json();
};

/**
 * Obtener listado de cesiones por estado
 * GET /api/Cesiones/getCessionsListing?cessionStatus={status}
 * Estados posibles: 'RECHAZADO', 'PENDIENTE', 'EN REVISION', 'APROBADO'
 */
export const getCesionesListing = async (status, sellerID) => {
  const response = await fetch(
    `${API_BASE_URL}/api/Cesiones/getCessionsListing?cessionStatus=${encodeURIComponent(status)}&sellerID=${encodeURIComponent(sellerID)}`,
    {
      method: 'GET',
      headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
    }
  );

  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.message || `Error al obtener listado: ${response.status}`);
  }

  const data = await response.json();
  return Array.isArray(data) ? data : [];
};

/**
 * Convertir un File a base64
 * @param {File} file
 * @returns {Promise<string>} Base64 sin el prefijo data:...
 */
/**
 * Validar enlace de comprador por hash
 * POST /api/Cesiones/validateBuyerLink
 */
export const validateBuyerLink = async ({ hash = '' }) => {
  const response = await fetch(`${API_BASE_URL}/api/Cesiones/validateBuyerLink`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ hash }),
  });

  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.message || `Error al validar enlace: ${response.status}`);
  }

  const data = await response.json();
  if (data.errMsg) throw new Error(data.errMsg);
  return data;
};

/**
 * Resetear un caso de cesión
 * POST /api/Cesiones/resetCase
 */
export const resetCase = async ({ group = '', order = '', cessionID = '' }) => {
  const response = await fetch(`${API_BASE_URL}/api/Cesiones/resetCase`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ group, order, cessionID }),
  });

  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.message || `Error al resetear el caso: ${response.status}`);
  }

  const data = await response.json().catch(() => ({}));
  if (data.errMsg) throw new Error(data.errMsg);
  return data;
};

export const fileToBase64 = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result;
      // Extraer solo la parte base64 (sin el prefijo "data:...;base64,")
      const base64 = result.includes(',') ? result.split(',')[1] : result;
      resolve(base64);
    };
    reader.onerror = () => reject(new Error('Error al leer el archivo'));
    reader.readAsDataURL(file);
  });

/**
 * Guardar información del formulario del comprador
 * POST /api/Cesiones/storeBuyerInformation
 */
export const storeBuyerInformation = async (payload) => {
  const response = await fetch(`${API_BASE_URL}/api/Cesiones/storeBuyerInformation`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.message || `Error al guardar información: ${response.status}`);
  }

  const data = await response.json().catch(() => ({}));
  if (data.errMsg) throw new Error(data.errMsg);
  return data;
};
